/**
 * Mikori — My Ritual (Refined).
 * Build flow → Active tracker. Anonymous, lives on the device.
 *
 * Storage (kept compatible with v1 so existing plans aren't lost):
 *   mikori_ritual_plan_v1     { products, schedule, goalWeeks, createdAt, intent }
 *   mikori_ritual_log_v1      [{ date, handle, done }]
 *   mikori_ritual_reflect_v1  { 'YYYY-MM-DD': 'note' }
 */

(function () {
  'use strict';

  const STORAGE_PLAN    = 'mikori_ritual_plan_v1';
  const STORAGE_LOG     = 'mikori_ritual_log_v1';
  const STORAGE_REFLECT = 'mikori_ritual_reflect_v1';

  const DAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const DAY_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const INTENTS = [
    { id: 'ground',  label: 'Ground',  sub: 'Calm + steady'   },
    { id: 'glow',    label: 'Glow',    sub: 'Skin + radiance' },
    { id: 'restore', label: 'Restore', sub: 'Repair + renew'  },
  ];

  const REFLECTION_PROMPTS = [
    'How did your skin feel after today?',
    'What part of the ritual felt easiest?',
    'What part of you needed the most care today?',
    'What surprised you about your hair this week?',
    'How is your scalp feeling lately?',
    'Where in your body did you feel the calm settle?',
    'What did you make space for today that you usually skip?'
  ];

  // ── Helpers ──────────────────────────────────────────────────────
  const $  = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  function todayStr() { return new Date().toISOString().split('T')[0]; }
  function parseDate(s) { return new Date(s + 'T00:00:00'); }
  function addDays(s, n) { const d = parseDate(s); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; }
  function dayCode(s) { return DAY_CODES[parseDate(s).getDay()]; }
  function dayIndex(s) { return parseDate(s).getDay(); }
  function startOfWeek(s) { const d = parseDate(s); d.setDate(d.getDate() - d.getDay()); return d.toISOString().split('T')[0]; }
  function weekDates(weekStart) { const arr = []; for (let i = 0; i < 7; i++) arr.push(addDays(weekStart, i)); return arr; }
  function escapeHtml(str) { const div = document.createElement('div'); div.textContent = str == null ? '' : String(str); return div.innerHTML; }

  // ── Storage ──────────────────────────────────────────────────────
  function loadPlan()  { try { return JSON.parse(localStorage.getItem(STORAGE_PLAN)); } catch (e) { return null; } }
  function savePlan(p) { localStorage.setItem(STORAGE_PLAN, JSON.stringify(p)); }
  function clearPlan() { localStorage.removeItem(STORAGE_PLAN); localStorage.removeItem(STORAGE_LOG); localStorage.removeItem(STORAGE_REFLECT); }

  function loadLog()    { try { return JSON.parse(localStorage.getItem(STORAGE_LOG)) || []; } catch (e) { return []; } }
  function saveLog(log) { localStorage.setItem(STORAGE_LOG, JSON.stringify(log)); }
  function isDone(date, handle) { return loadLog().some(e => e.date === date && e.handle === handle && e.done); }
  function toggleDone(date, handle) {
    const log = loadLog();
    const idx = log.findIndex(e => e.date === date && e.handle === handle);
    if (idx >= 0) log[idx].done = !log[idx].done;
    else log.push({ date, handle, done: true });
    saveLog(log);
  }

  function loadReflections() { try { return JSON.parse(localStorage.getItem(STORAGE_REFLECT)) || {}; } catch (e) { return {}; } }
  function saveReflection(date, text) {
    const r = loadReflections();
    if (text && text.trim()) r[date] = text.trim(); else delete r[date];
    localStorage.setItem(STORAGE_REFLECT, JSON.stringify(r));
  }

  // ── Stats ────────────────────────────────────────────────────────
  function currentStreak() {
    const doneDates = new Set(loadLog().filter(e => e.done).map(e => e.date));
    let streak = 0; let d = todayStr();
    while (doneDates.has(d)) { streak++; d = addDays(d, -1); }
    return streak;
  }
  function weekCompletionPaireed(weekStart, plan) {
    let scheduled = 0, completed = 0;
    weekDates(weekStart).forEach(date => {
      const code = dayCode(date);
      plan.products.forEach(h => {
        if ((plan.schedule[h] || []).includes(code)) {
          scheduled++;
          if (isDone(date, h)) completed++;
        }
      });
    });
    return { scheduled, completed, pct: scheduled === 0 ? 0 : Math.round(completed / scheduled * 100) };
  }
  function lastNWeeksAvg(n, plan) {
    const ws = startOfWeek(todayStr());
    let totalSched = 0, totalDone = 0;
    for (let i = 0; i < n; i++) {
      const start = addDays(ws, -7 * i);
      const wc = weekCompletionPaireed(start, plan);
      totalSched += wc.scheduled;
      totalDone += wc.completed;
    }
    return totalSched === 0 ? 0 : Math.round(totalDone / totalSched * 100);
  }
  function weeksSince(planCreatedAt) {
    if (!planCreatedAt) return 1;
    const start = parseDate(planCreatedAt);
    const now = new Date();
    const diffDays = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.floor(diffDays / 7) + 1);
  }

  // ── Milestones (refined: ceremonial markers, not gamified) ───────
  function milestones(plan) {
    const goal = plan.goalWeeks || 8;
    const wk = Math.min(weeksSince(plan.createdAt), goal);
    const log = loadLog();
    const doneDays = new Set(log.filter(e => e.done).map(e => e.date)).size;
    return [
      { label: 'First week',  sub: 'Showed up',     icon: '•',   on: doneDays >= 5 },
      { label: 'Two weeks',   sub: 'Habit forming', icon: '◦◦',  on: wk >= 2 && doneDays >= 8 },
      { label: 'Halfway',     sub: `Week ${Math.ceil(goal / 2)}`,    icon: '↗',   on: wk >= Math.ceil(goal / 2) },
      { label: 'Glow up',     sub: `Week ${Math.max(2, goal - 2)}`,  icon: '✺',   on: wk >= goal - 2 },
      { label: 'Full moon',   sub: `Week ${goal}`,  icon: '○',   on: wk >= goal },
      { label: 'Tribe',       sub: 'Share + inspire', icon: '❋', on: wk >= goal },
    ];
  }

  // ── Reflection prompt: stable per-day pick ───────────────────────
  function reflectionPromptForDate(date) {
    let h = 0;
    for (let i = 0; i < date.length; i++) h = ((h << 5) - h + date.charCodeAt(i)) | 0;
    return REFLECTION_PROMPTS[Math.abs(h) % REFLECTION_PROMPTS.length];
  }

  // ── Top-level render ─────────────────────────────────────────────
  function render() {
    const root = $('#mk-ritual-root');
    if (!root) return;
    const plan = loadPlan();
    if (!plan || !plan.products || plan.products.length === 0) {
      renderBuilder(root);
    } else {
      renderTracker(root, plan);
    }
  }

  // ── Builder ──────────────────────────────────────────────────────
  function renderBuilder(root, draftPlan) {
    const products = window.MIKORI_PRODUCTS || [];
    const cfg = window.MIKORI_RITUAL_CONFIG || {};
    const goalDefault = cfg.goalWeeksDefault || 8;
    const urlParams = new URLSearchParams(window.location.search);
    const preselect = urlParams.get('add');

    const existing = draftPlan || loadPlan();
    let intent   = (existing && existing.intent) || 'ground';
    let selected = (existing && existing.products) ? existing.products.slice() : [];
    let schedule = Object.assign({}, (existing && existing.schedule) || {});
    let goal     = (existing && existing.goalWeeks) || goalDefault;

    if (!selected.length && preselect && products.find(p => p.handle === preselect)) {
      selected = [preselect];
    }
    selected.forEach(h => { if (!schedule[h]) schedule[h] = []; });

    function totalSessions() {
      return selected.reduce((sum, h) => sum + ((schedule[h] || []).length), 0);
    }

    function paint() {
      root.innerHTML = `
        <div class="mkr-pagehead">
          <span class="mkr-pagehead__t">My Ritual</span>
        </div>

        <div class="hero-card">
          <div class="hero-stripes" aria-hidden="true"></div>
          <div class="hero-eyebrow">— Personalise —</div>
          <h1 class="hero-title">Build your<br/><em>ritual</em>.</h1>
          <p class="hero-sub">Three slow steps. We'll keep you consistent — gently.</p>
          <div class="hero-meta">
            <span><b>${selected.length}</b>/${products.length || 6} products</span>
            <span class="dot"></span>
            <span><b>${totalSessions()}</b> sessions/wk</span>
            <span class="dot"></span>
            <span><b>${goal}</b>w goal</span>
          </div>
        </div>

        <div class="step">
          <div class="step__n">01</div>
          <div class="step__txt"><div class="step__t">Set your intent</div><div class="step__s">What does this season ask of you?</div></div>
          <div class="step__line"></div>
        </div>
        <div class="intent-row">
          ${INTENTS.map(i => `
            <button type="button" class="intent ${intent === i.id ? 'is-on' : ''}" data-intent="${i.id}">
              <span class="intent__glyph" aria-hidden="true"></span>
              <span class="intent__label">${i.label}</span>
              <span class="intent__sub">${i.sub}</span>
            </button>
          `).join('')}
        </div>

        <div class="step">
          <div class="step__n">02</div>
          <div class="step__txt"><div class="step__t">Choose your products</div><div class="step__s">${selected.length} selected</div></div>
          <div class="step__line"></div>
        </div>
        <div class="prod-grid">
          ${products.map(p => {
            const on = selected.includes(p.handle);
            return `
              <button type="button" class="prod ${on ? 'is-on' : ''}" data-product="${escapeHtml(p.handle)}">
                <div class="prod__media" style="background-image:url('${escapeHtml(p.image || '')}')">
                  <span class="prod__check" aria-hidden="true">${on ? '✓' : ''}</span>
                </div>
                <div class="prod__body">
                  <div class="prod__kind">${escapeHtml(p.kind || 'Ritual')}</div>
                  <div class="prod__name">${escapeHtml(p.title)}</div>
                </div>
              </button>
            `;
          }).join('')}
        </div>

        <div class="step">
          <div class="step__n">03</div>
          <div class="step__txt"><div class="step__t">Set your weekly cadence</div><div class="step__s">Tap days you'll show up</div></div>
          <div class="step__line"></div>
        </div>
        <div class="sched">
          ${selected.length === 0
            ? `<div class="sched__empty">Choose at least one product above.</div>`
            : selected.map(h => {
                const p = products.find(x => x.handle === h);
                if (!p) return '';
                const days = schedule[h] || [];
                return `
                  <div class="sched__row">
                    <div class="sched__head">
                      <div class="sched__thumb" style="background-image:url('${escapeHtml(p.image || '')}')"></div>
                      <div class="sched__label">
                        <div class="sched__kind">${escapeHtml(p.kind || 'Ritual')}</div>
                        <div class="sched__name">${escapeHtml(p.title)}</div>
                      </div>
                      <div class="sched__count">${days.length}<span>/wk</span></div>
                    </div>
                    <div class="sched__days">
                      ${DAY_LETTERS.map((d, i) => `
                        <button type="button" class="day ${days.includes(DAY_CODES[i]) ? 'is-on' : ''}" data-product="${escapeHtml(h)}" data-day="${DAY_CODES[i]}" aria-label="${DAY_FULL[i]}">${d}</button>
                      `).join('')}
                    </div>
                  </div>
                `;
              }).join('')
          }
        </div>

        <div class="step">
          <div class="step__n">04</div>
          <div class="step__txt"><div class="step__t">Commit a window</div><div class="step__s">Habits form between 4–12 weeks</div></div>
          <div class="step__line"></div>
        </div>
        <div class="goal-row">
          ${[4, 8, 12].map(w => `
            <button type="button" class="goal ${goal === w ? 'is-on' : ''}" data-goal="${w}">
              <span class="goal__num">${w}</span>
              <span class="goal__lab">weeks</span>
              ${w === 8 ? '<span class="goal__tag">Most chosen</span>' : ''}
            </button>
          `).join('')}
        </div>

        <div class="commit">
          <div class="commit__line">
            <span class="commit__lab">Your ritual</span>
            <span class="commit__val">${selected.length} products · ${totalSessions()}/wk · ${goal}w</span>
          </div>
          <button type="button" class="cta" id="mkr-begin" ${selected.length === 0 || totalSessions() === 0 ? 'disabled style="opacity:.55;cursor:not-allowed"' : ''}>Begin my ritual <span aria-hidden="true">→</span></button>
          <div class="commit__fine">No reminders unless you ask. We won't email you to feel bad.</div>
        </div>
      `;

      // Bind events on each paint (lightweight; the tree is small).
      $$('[data-intent]', root).forEach(el => el.addEventListener('click', () => {
        intent = el.dataset.intent;
        paint();
      }));
      $$('[data-product]', root).forEach(el => {
        if (el.classList.contains('day')) return;
        el.addEventListener('click', () => {
          const h = el.dataset.product;
          const idx = selected.indexOf(h);
          if (idx >= 0) selected.splice(idx, 1);
          else { selected.push(h); if (!schedule[h]) schedule[h] = []; }
          paint();
        });
      });
      $$('.day', root).forEach(el => el.addEventListener('click', () => {
        const h = el.dataset.product;
        const d = el.dataset.day;
        const arr = schedule[h] || [];
        const i = arr.indexOf(d);
        if (i >= 0) arr.splice(i, 1); else arr.push(d);
        schedule[h] = arr;
        paint();
      }));
      $$('[data-goal]', root).forEach(el => el.addEventListener('click', () => {
        goal = parseInt(el.dataset.goal, 10);
        paint();
      }));
      const beginBtn = $('#mkr-begin', root);
      if (beginBtn) beginBtn.addEventListener('click', () => {
        if (selected.length === 0 || totalSessions() === 0) return;
        savePlan({
          products: selected,
          schedule,
          goalWeeks: goal,
          intent,
          createdAt: existing && existing.createdAt ? existing.createdAt : todayStr()
        });
        render();
        scrollToTop();
      });
    }

    paint();
  }

  // ── Tracker ──────────────────────────────────────────────────────
  function renderTracker(root, plan) {
    const products = window.MIKORI_PRODUCTS || [];
    const today = todayStr();
    const todayIdx = dayIndex(today);
    const ws = startOfWeek(today);
    const week = weekDates(ws).map((d, i) => {
      const code = DAY_CODES[i];
      const planned = plan.products.filter(h => (plan.schedule[h] || []).includes(code));
      return { date: d, dayIdx: i, name: DAY_FULL[i], num: parseDate(d).getDate(), planned };
    });
    const goal = plan.goalWeeks || 8;
    const currentWeek = Math.min(weeksSince(plan.createdAt), goal);

    const wc = weekCompletionPaireed(ws, plan);
    const consistency = lastNWeeksAvg(4, plan);
    const streak = currentStreak();

    const todayDay = week.find(w => w.dayIdx === todayIdx) || week[0];
    const todaysItems = todayDay.planned
      .map(h => ({ p: products.find(x => x.handle === h), handle: h }))
      .filter(x => x.p);

    const calStyleAttr = root.getAttribute('data-calendar-style') || 'strip';

    const reflectPrompt = reflectionPromptForDate(today);
    const reflectExisting = (loadReflections() || {})[today] || '';

    root.innerHTML = `
      <div class="mkr-pagehead">
        <span class="mkr-pagehead__t">My Ritual</span>
        <span class="mkr-pagehead__s">· Week ${currentWeek} of ${goal}</span>
      </div>

      <div class="today">
        <div class="today__top">
          <div>
            <div class="today__brow">— Today, ${DAY_FULL[todayIdx]} —</div>
            <h1 class="today__title">Show up,<br/><em>softly.</em></h1>
          </div>
          <div class="today__streak">
            ${flameSvg()}
            <div>
              <div class="today__streak-num">${streak}</div>
              <div class="today__streak-lab">day streak</div>
            </div>
          </div>
        </div>
        <div class="today__items">
          ${todaysItems.length === 0
            ? `<div class="today__empty">Nothing scheduled for today. Rest is a ritual too.</div>`
            : todaysItems.map(({ p, handle }) => {
                const done = isDone(today, handle);
                return `
                  <button type="button" class="today-item ${done ? 'is-done' : ''}" data-toggle-today="${escapeHtml(handle)}">
                    <div class="today-item__media" style="background-image:url('${escapeHtml(p.image || '')}')"></div>
                    <div class="today-item__body">
                      <div class="today-item__kind">${escapeHtml(p.kind || 'Ritual')}</div>
                      <div class="today-item__name">${escapeHtml(p.title)}</div>
                    </div>
                    <div class="today-item__check" aria-hidden="true">
                      ${done ? checkSvg(false) : '<span class="today-item__circle"></span>'}
                    </div>
                  </button>
                `;
              }).join('')
          }
        </div>
      </div>

      <div class="stat-row">
        ${statBlock(`${wc.pct}%`, 'this week', `${wc.completed}/${wc.scheduled} sessions`, wc.pct)}
        ${statBlock(`${currentWeek}/${goal}`, 'weeks in', `${Math.max(0, goal - currentWeek)} to go`, Math.round(currentWeek / goal * 100))}
        ${statBlock(`${consistency}%`, 'consistency', 'last 4 weeks', consistency)}
      </div>

      <div class="sh">
        <div>
          <div class="sh__brow">— This Week —</div>
          <div class="sh__title">Your cadence</div>
        </div>
      </div>
      ${renderCalendar(calStyleAttr, week, products, todayIdx)}

      <div class="sh">
        <div>
          <div class="sh__brow">— The Journey —</div>
          <div class="sh__title">${goal}-week commitment</div>
        </div>
      </div>
      <div class="journey">
        <div class="journey__bar"><div class="journey__fill" style="width:${Math.min(100, currentWeek / goal * 100)}%"></div></div>
        <div class="journey__weeks" style="grid-template-columns:repeat(${goal}, 1fr)">
          ${Array.from({ length: goal }, (_, i) => {
            const w = i + 1;
            const state = w < currentWeek ? 'past' : w === currentWeek ? 'now' : 'future';
            return `
              <div class="journey__w journey__w--${state}">
                <div class="journey__node"></div>
                <div class="journey__num">W${w}</div>
              </div>
            `;
          }).join('')}
        </div>
      </div>

      <div class="sh">
        <div>
          <div class="sh__brow">— Markers —</div>
          <div class="sh__title">Milestones</div>
        </div>
      </div>
      <div class="mile-row">
        ${milestones(plan).map(m => `
          <div class="mile ${m.on ? 'is-on' : ''}">
            <div class="mile__icon">${escapeHtml(m.icon)}</div>
            <div class="mile__lab">${escapeHtml(m.label)}</div>
            <div class="mile__sub">${escapeHtml(m.sub)}</div>
          </div>
        `).join('')}
      </div>

      <div class="sh">
        <div>
          <div class="sh__brow">— Notes —</div>
          <div class="sh__title">Tonight's reflection</div>
        </div>
      </div>
      <div class="reflect">
        <div class="reflect__q">${escapeHtml(reflectPrompt)}</div>
        <textarea class="reflect__input" id="mkr-reflect" placeholder="A line or two — for you only.">${escapeHtml(reflectExisting)}</textarea>
        <div class="reflect__row">
          <span class="reflect__hint">Private. Lives on this device.</span>
          <button type="button" class="reflect__save" id="mkr-reflect-save">Save</button>
        </div>
      </div>

      <div class="active-foot">
        <button type="button" id="mkr-edit">Edit ritual</button>
        <button type="button" id="mkr-reset">Start over</button>
      </div>
    `;

    // Bind interactions
    $$('[data-toggle-today]', root).forEach(btn => btn.addEventListener('click', () => {
      toggleDone(today, btn.dataset.toggleToday);
      renderTracker(root, loadPlan());
    }));
    $$('[data-toggle-grid]', root).forEach(btn => btn.addEventListener('click', () => {
      const date = btn.dataset.date;
      const handle = btn.dataset.toggleGrid;
      toggleDone(date, handle);
      renderTracker(root, loadPlan());
    }));
    const reflectBtn = $('#mkr-reflect-save', root);
    const reflectEl  = $('#mkr-reflect', root);
    if (reflectBtn && reflectEl) {
      reflectBtn.addEventListener('click', () => {
        saveReflection(today, reflectEl.value);
        reflectBtn.textContent = 'Saved';
        reflectBtn.classList.add('is-saved');
        setTimeout(() => {
          reflectBtn.textContent = 'Save';
          reflectBtn.classList.remove('is-saved');
        }, 1400);
      });
    }
    const editBtn  = $('#mkr-edit', root);
    const resetBtn = $('#mkr-reset', root);
    if (editBtn) editBtn.addEventListener('click', () => {
      renderBuilder(root, loadPlan());
      scrollToTop();
    });
    if (resetBtn) resetBtn.addEventListener('click', () => {
      if (confirm('This clears your ritual and progress. Continue?')) {
        clearPlan();
        render();
      }
    });
  }

  // ── Calendar variants ────────────────────────────────────────────
  function renderCalendar(style, week, products, todayIdx) {
    if (style === 'moon') return renderCalMoon(week, todayIdx);
    if (style === 'grid') return renderCalGrid(week, products, todayIdx);
    return renderCalStrip(week, todayIdx);
  }

  function ringFor(d) {
    const completed = (loadLog().filter(e => e.done && e.date === d.date)
                        .filter(e => d.planned.includes(e.handle))).length;
    const planned = d.planned.length;
    const pct = planned ? completed / planned : 0;
    return { completed, planned, pct };
  }

  function renderCalStrip(week, todayIdx) {
    return `
      <div class="cal-strip">
        ${week.map(d => {
          const { completed, planned, pct } = ringFor(d);
          const isToday = d.dayIdx === todayIdx;
          const full = planned > 0 && completed === planned;
          return `
            <div class="cal-strip__day ${isToday ? 'is-today' : ''} ${full ? 'is-full' : ''}">
              <div class="cal-strip__name">${d.name}</div>
              <div class="cal-strip__date">${d.num}</div>
              <div class="cal-strip__ring" style="--p:${pct * 360}deg">
                <div class="cal-strip__ring-inner">
                  ${full ? checkSvg(true) : `<span>${completed}/${planned}</span>`}
                </div>
              </div>
              <div class="cal-strip__dots">
                ${d.planned.map(h => {
                  const isOn = isDone(d.date, h);
                  return `<span class="cal-strip__dot ${isOn ? 'is-on' : ''}"></span>`;
                }).join('')}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  function renderCalMoon(week, todayIdx) {
    return `
      <div class="cal-moon">
        ${week.map(d => {
          const { completed, planned, pct } = ringFor(d);
          const isToday = d.dayIdx === todayIdx;
          return `
            <div class="cal-moon__day ${isToday ? 'is-today' : ''}">
              <div class="cal-moon__name">${d.name}</div>
              <div class="cal-moon__moon">
                <div class="cal-moon__shadow" style="width:${100 - pct * 100}%"></div>
                <div class="cal-moon__date">${d.num}</div>
              </div>
              <div class="cal-moon__count">${completed}/${planned}</div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  function renderCalGrid(week, products, todayIdx) {
    return `
      <div class="cal-grid">
        ${week.map(d => {
          const isToday = d.dayIdx === todayIdx;
          return `
            <div class="cal-grid__day ${isToday ? 'is-today' : ''}">
              <div class="cal-grid__head">
                <span class="cal-grid__name">${d.name}</span>
                <span class="cal-grid__date">${d.num}</span>
              </div>
              <div class="cal-grid__list">
                ${d.planned.map(h => {
                  const p = products.find(x => x.handle === h);
                  const done = isDone(d.date, h);
                  const short = p ? (p.short || (p.title || '').split(' ')[0]) : h;
                  return `
                    <button type="button" class="cal-grid__pill ${done ? 'is-done' : ''}" data-toggle-grid="${escapeHtml(h)}" data-date="${d.date}" title="${escapeHtml(p ? p.title : h)}">
                      <span class="cal-grid__pill-dot"></span>
                      <span class="cal-grid__pill-name">${escapeHtml(short)}</span>
                    </button>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  // ── Atoms ────────────────────────────────────────────────────────
  function statBlock(big, label, sub, progress) {
    return `
      <div class="stat">
        <div class="stat__big">${escapeHtml(big)}</div>
        <div class="stat__lab">${escapeHtml(label)}</div>
        <div class="stat__bar"><div class="stat__fill" style="width:${Math.min(100, Math.max(0, progress || 0))}%"></div></div>
        <div class="stat__sub">${escapeHtml(sub)}</div>
      </div>
    `;
  }
  function flameSvg() {
    return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M12 2c1 3 4 4 4 8a4 4 0 0 1-8 0c0-2 1-3 2-4-1 5 2 6 2 6"/><path d="M12 14a4 4 0 0 0-4 4 4 4 0 0 0 8 0c0-1.5-.5-2-1-2.5"/></svg>`;
  }
  function checkSvg(small) {
    const s = small ? 10 : 14;
    return `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>`;
  }

  function scrollToTop() {
    const el = $('#mk-ritual-root');
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Init ─────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }
  window.MikoriRitual = { render, loadPlan, loadLog, clearPlan };
})();
